import { ReservationStatus } from "@common/enum";
import { IFlightSegment, ITraveler, Locality, TravelDocType } from "@common/models";
import { ICmsAncillary } from "@common/models/CMS";
import { IShareInitializeResponse } from "@common/models/ShareInitializeResponse";
import { ISharingRequest } from "@common/models/SharingRequest";
import { ISharingResponse } from "@common/models/SharingResponse";
import Button from "@components/general/form/Button";
import Price from "@components/Price";
import SSRIcon from "@components/SSRIcon";
import { Culture } from "@core/index";
import { IAppState } from "@misc/State";
import Sharing from "@pages/Sharing";
import Notification from "@service/Notification";
import { Action, initialShareTraveler, ShareTraveler } from "@service/proxy";
import Translate from "@service/Translate";
import { getAirport } from "@utilize/Format";
import { FC, Fragment, useState } from "react";
import { useSelector } from "react-redux";

interface IPassengerInfoItemProps {
    traveler: ITraveler;
    locality: Locality;
}

const PassengerInfoItem: FC<IPassengerInfoItemProps> = ({ traveler, locality }) => {
    const { tr } = Translate;
    const [shareInitializeResponse, setShareInitializeResponse] = useState<IShareInitializeResponse>();
    const [sharingDialogVisibility, setSharingDialogVisibility] = useState<boolean>(false);
    const { cmsAncillaries, pnrInfo } = useSelector((state: IAppState) => ({
        cmsAncillaries: state.cmsAncillaries,
        pnrInfo: state.pnrInfo,
    }));

    const segments: IFlightSegment[] = [];
    const visibleFlight = pnrInfo?.originDestinations?.filter(o => o.visibility && o.status !== ReservationStatus.Cancel) || [];
    for (const od of visibleFlight)
        for (const flightSegment of od.flightSegments)
            segments.push(flightSegment);

    const onTavelerSharingInit = (traveler: ITraveler): Promise<boolean> => {
        return new Promise((resolve) => {
            const sharingRequest: ISharingRequest = {
                pnr: pnrInfo.pnr,
                traveler
            };
            initialShareTraveler(Action.POST, { data: sharingRequest })
                .then((response) => {
                    setShareInitializeResponse(response.content);
                    setSharingDialogVisibility(true);
                })
                .catch(() => { })
                .finally(() => {
                    resolve(true);
                })
        })
    }

    const onTravelerSharing = (sharingRequest: ISharingRequest): Promise<ISharingResponse> => {
        return new Promise((resolve, reject) => {
            sharingRequest.pnr = pnrInfo.pnr;
            ShareTraveler(Action.POST, { data: sharingRequest })
                .then((response) => {
                    resolve(response.content);
                    Notification.success("itinerary_sharing_msg_successful");
                })
                .catch((error) => {
                    reject();
                })
        }
        )
    }
    const renderAncillaryPerSegment = (segment: IFlightSegment, traveler: ITraveler, index: number) => {
        const selectedItems: ICmsAncillary[] = [];
        pnrInfo?.selectedAncillaries?.forEach(selectedAncillary =>
            selectedAncillary.preferences?.find(preference => traveler.refNumber === preference.assignee?.travelerRefNumber)
                ?.selections?.find(selectItem => selectItem.scope?.segmentRPH === segment.rph)
                ?.selectedItems?.filter(item => item.code && item.code !== "FQTV")
                .forEach(item => {
                    if (selectedAncillary.type === "AIRPORT_TRANSFER")
                        selectedItems.push(cmsAncillaries.find(x => x.code?.code === "TRNS") || {});
                    else if (selectedAncillary.type === "MEAL")
                        selectedItems.push(cmsAncillaries.find(x => x.code?.code === "MEAL") || {});
                    else
                        selectedItems.push(cmsAncillaries.find(x => x.code?.code === item.code) || {});
                })
        );

        return selectedItems.length ?
            <div key={index} className="col-1-auto">
                <div className="segment-route">
                    <div>{getAirport(segment.origin?.code).cityName}/{getAirport(segment.destination?.code).cityName}: </div>
                    <div className="selected-items">
                        {
                            selectedItems.map((item, index) =>
                                <div key={index} className="selected-item">
                                    <SSRIcon
                                        title={item.name || ""}
                                        icon={`icon-${item.icon}`}
                                    />
                                </div>
                            )
                        }
                    </div>
                </div>
            </div>
            : null;
    }
    const hasAvailableAnsillary = pnrInfo?.selectedAncillaries?.find(a => a.preferences?.find(p => p.assignee?.travelerRefNumber === traveler.refNumber && p.selections?.find(s => s.selectedItems?.find(si => si.code !== "FQTV"))));
    const fare = pnrInfo?.pricingInfo?.fareBreakdown?.find(fb => fb.travelerRefNumbers?.includes(traveler.refNumber || "")) || {};
    const sameTravelerWithContact = traveler.firstName === pnrInfo.contactPerson?.firstName && traveler.lastName === pnrInfo.contactPerson?.lastName;

    return (
        <div className="passenger-details-body">
            <div className="row spa-0">
                <div className="col-1-1 flex flex-center">
                    <div className={`row flex flex-grow spa-0${hasAvailableAnsillary ? "" : " padding-bottom"}`}>
                        <div className="col-1-auto col-md-1-2 col-sm-1-2 passenger-details-body-item name trim">
                            <div className="title-responsive">{tr("full_name")}</div>
                            <div className="trim">{`${traveler.prefix} ${traveler.firstName} ${traveler.lastName}`}</div>
                        </div>
                        <div className="col-1-auto col-md-1-2 col-sm-1-2 passenger-details-body-item ticket-number">
                            <div className="title-responsive">{tr("ticket_number")}</div>
                            <div>{traveler.tickets && traveler.tickets[0] ? traveler.tickets[0].ticketNo : "-"}</div>
                        </div>
                        <div className="col-1-auto col-md-1-2 col-sm-1-2 passenger-details-body-item passport-id">
                            <div className="title-responsive">{tr(locality === Locality.International ? "passport_code" : "national_code")}</div>
                            <div className={`${locality === Locality.International ? " en" : ""}`}>
                                {traveler.travelDocs?.find(t => locality === Locality.International ? t.documentType === TravelDocType.PSPT : t.documentType === TravelDocType.IDCD)?.id || '-'}
                            </div>
                        </div>
                        {
                            <div className="col-1-auto col-md-1-1 col-sm-1-1 passenger-details-body-item sharing-btn">
                                {
                                    sameTravelerWithContact ? "" :
                                        <>
                                            <Button
                                                text={tr("btn_ticket_send")}
                                                variant="outlined"
                                                color="primary"
                                                height="30px"
                                                onClick={() => { onTavelerSharingInit(traveler) }}
                                            />
                                            <Sharing
                                                show={sharingDialogVisibility}
                                                traveler={traveler}
                                                dataSource={pnrInfo?.travelers?.concat(pnrInfo.contactPerson as ITraveler)}
                                                shareInitializeResponse={shareInitializeResponse}
                                                onSharingClick={onTravelerSharing}
                                                onCloseClick={() => setSharingDialogVisibility(false)}
                                            />
                                        </>
                                }
                            </div>
                        }
                        <div className="col-1-auto col-md-1-2 col-sm-1-2 passenger-details-body-item price-section">
                            <div className="title-responsive">{tr("ticket")} ({Culture.getLocale().currency.displayText})</div>
                            <Price amount={fare.unitPrice} />
                        </div>
                    </div>
                </div>
            </div>
            {
                hasAvailableAnsillary ?
                    <div className={`flex col-1-1 ssr-items${sameTravelerWithContact ? '' : ' has-share-btn'}`}>
                        <div className="ssr-items-title">
                            {tr("stepper_booking_special_services_title")}
                        </div>
                        <div className="row spa-2 flex flex-center ssr-items-content">
                            {segments.map((segment, index) => renderAncillaryPerSegment(segment, traveler, index))}
                        </div>
                    </div>
                    : ""
            }

        </div>
    )
}

export default PassengerInfoItem;